import { createContext, useEffect, useRef, useState } from 'react';
import {
  type AnimationResult,
  type SpringValue,
  type UnknownProps,
} from '@react-spring/web';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Dimension, BaseModal } from '@hungryroot/ramen';
import { useIdentify } from '@hungryroot/rest-hooks';
import { invalidateRatablePairings } from '../../hooks/useSelfRatablePairings';
import { invalidateRatableProductsFeedbackModal } from '../../hooks/useSelfRatableProductsFeedbackModal';
import { LoadingStage } from './LoadingStage/LoadingStage';
import { NeedHelpWithYourOrderStage } from './NeedHelpWithYourOrderStage/NeedHelpWithYourOrderStage';
import {
  PerceivedValueStage,
  useShowPerceivedValueStage,
} from './PerceivedValueStage/PerceivedValueStage';
import { RatingStage } from './RatingStage/RatingStage';
import { ThankYouStage } from './ThankYouStage/ThankYouStage';
import {
  useFoodFeedbackModal,
  Stage,
  type UseFoodFeedbackModalProps,
} from './useFoodFeedbackModal';
import {
  type RatablePairingItem,
  type RatableProductItem,
} from './useRatableItems';
import styles from './FoodFeedbackModal.module.css';

type FoodFeedbackModalProps = {
  dismissModal: NonNullable<UseFoodFeedbackModalProps['dismissModal']>;
  orderId?: number;
  recentOrderCount?: number;
  onRating?: ({
    item,
    rating,
  }: {
    item: RatablePairingItem | RatableProductItem;
    rating: boolean | null;
  }) => void;
  onShow?: () => void;
};

function FoodFeedbackModalWithoutContext(props: FoodFeedbackModalProps) {
  const {
    dismissModal: passedDismissModal,
    orderId,
    recentOrderCount,
    onRating,
    onShow,
  } = props;

  const {
    pairings,
    products,
    prevStage,
    nextStage,
    stage,
    changeDetection,
    itemCounts,
    onRating: ratedItemCountOnRating,
  } = useFoodFeedbackModal({
    orderId,
    recentOrderCount,
    dismissModal: passedDismissModal,
  });

  // This may seem redundant at first. Controlling visibility within this
  // component allows us to control the animation of the modal and allow
  // the consumer to unmount the modal when it is no longer needed without
  // needing to manage the modal's visibility and animation state.
  const [visible, setVisible] = useState(true);

  const [ratedItemsCount, setRatedItemsCount] = useState(0);

  const { shouldRender: showPerceivedValue, questionCount } =
    useShowPerceivedValueStage();

  const totalItemsCount =
    pairings.length +
    products.length +
    (showPerceivedValue ? questionCount : 0);

  const queryClient = useQueryClient();

  const { customerData } = useIdentify();

  useEffect(() => {
    if (visible && stage !== Stage.Loading) {
      onShow?.();
    }
  }, [onShow, visible]);

  return (
    <Dimension
      as={BaseModal}
      level="three"
      onDismiss={() => setVisible(false)}
      isOpen={visible}
      ariaLabel="Food Rating Modal"
      contentClassName={styles['modal-content']}
      innerContentClassName={clsx({
        [styles['modal-inner-content']]: true,
        [styles['modal-inner-content-stage-thank-you']]:
          stage === Stage.ThankYou,
      })}
      onRest={(animationResult: AnimationResult<SpringValue<UnknownProps>>) => {
        if (animationResult?.value?.opacity === 0) {
          passedDismissModal({
            counts: itemCounts,
            dismissalType: stage === Stage.ThankYou ? 'submit' : 'close',
          });

          if (customerData?.id) {
            invalidateRatableProductsFeedbackModal({
              client: queryClient,
              customerId: customerData?.id,
            });
            invalidateRatablePairings({
              client: queryClient,
              customerId: customerData?.id,
            });
          }
        }
      }}
    >
      <LoadingStage visible={stage === Stage.Loading} />
      <RatingStage
        products={products}
        pairings={pairings}
        stage={stage}
        prevStage={prevStage}
        nextStage={nextStage}
        dismissModal={() => setVisible(false)}
        changeDetection={changeDetection}
        onRating={({ item, rating }) => {
          onRating?.({ item, rating });
          ratedItemCountOnRating({ item, rating });
        }}
        ratedItemsState={{
          ratedItemsCount,
          setRatedItemsCount,
        }}
        totalItemsCount={totalItemsCount}
      />
      {showPerceivedValue && (
        <PerceivedValueStage
          nextStage={nextStage}
          prevStage={prevStage}
          visible={stage === Stage.PerceivedValue}
          dismissModal={() => setVisible(false)}
          ratedItemsState={{
            ratedItemsCount,
            setRatedItemsCount,
          }}
          totalItemsCount={totalItemsCount}
          changeDetection={changeDetection}
        />
      )}

      <ThankYouStage
        dismissModal={() => {
          passedDismissModal({
            counts: itemCounts,
            dismissalType: 'submit',
          });
          setVisible(false);
        }}
        visible={stage === Stage.ThankYou}
      />
      <NeedHelpWithYourOrderStage
        prevStage={prevStage}
        nextStage={nextStage}
        dismissModal={() => setVisible(false)}
        visible={stage === Stage.NeedHelpWithYourOrder}
      />
    </Dimension>
  );
}

export const FoodFeedbackModalContext = createContext<{ issues?: Set<number> }>(
  {}
);

export function FoodFeedbackModal(props: FoodFeedbackModalProps) {
  const issuesRef = useRef<Set<number>>(new Set());

  return (
    // Used to prevent prop drilling. This context is meant to store values
    // that should not cause any re-renders when they change. All we really
    // care about are the values at the time of usage.
    <FoodFeedbackModalContext.Provider value={{ issues: issuesRef.current }}>
      <FoodFeedbackModalWithoutContext {...props} />
    </FoodFeedbackModalContext.Provider>
  );
}
